import { Box, Button, Card, Divider, Modal, ModalClose, ModalDialog, Stack, Typography } from '@healthinal/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { request } from '../../../../../api/axios.ts';
import { getGetTracingContactQueryKey } from '../../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { PatientDto } from '../../../../../api/generated/model/patientDto.ts';
import {
  getGetPatientBasicsByIdQueryKey,
  getGetPatientByIdQueryKey,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { SyncListAction } from '../../../../../components/SyncListAction.tsx';
import { EditTracingContactDetailsForm } from './EditTracingContactDetailsForm.tsx';

export function TracingContactDetailsCard({ tracingContactId }: { tracingContactId: string }) {
  const queryClient = useQueryClient();
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [formVersion, setFormVersion] = useState(0);

  const syncPersonMutation = useMutation({
    mutationFn: () =>
      request<PatientDto>({
        url: `/api/patients/${tracingContactId}/sync-person`,
        method: 'POST',
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(getGetPatientByIdQueryKey(tracingContactId), data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetPatientBasicsByIdQueryKey(tracingContactId) }),
        queryClient.invalidateQueries({ queryKey: getGetTracingContactQueryKey(tracingContactId) }),
      ]);

      setFormVersion((prev) => prev + 1);
      setSyncDialogOpen(false);
      toast.success('Personeninformationen wurden erfolgreich synchronisiert');
    },
    onError: () => {
      toast.error('Beim Synchronisieren der Personeninformationen ist ein Fehler aufgetreten');
    },
  });

  return (
    <Card>
      <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
        <Typography level="title-lg">Personalien</Typography>
        <SyncListAction
          loading={syncPersonMutation.isPending}
          onClick={() => setSyncDialogOpen(true)}
          title="Person synchronisieren"
        />
      </Box>
      <Divider />
      <EditTracingContactDetailsForm key={`${tracingContactId}-${formVersion}`} tracingContactId={tracingContactId} />

      <Modal open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Synchronisierung von Personeninformationen</Typography>
          <Typography>Möchten Sie wirklich die Personeninformationen synchronisieren?</Typography>
          <Stack direction="row" gap={2} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" color="neutral" onClick={() => setSyncDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button loading={syncPersonMutation.isPending} onClick={() => syncPersonMutation.mutate()}>
              Bestätigen
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Card>
  );
}
